import { MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DropdownMenu, { DropdownMenuItem } from './DropdownMenu';

export interface RowActionsMenuProps {
    /** Names the row in the trigger's accessible label ("Weitere Aktionen für …"). */
    rowName: string;
    /** The row's changing actions, in order. Hidden items are dropped. */
    items: DropdownMenuItem[];
    /** Destructive actions (Löschen, Entfernen, …): always last, behind a divider. */
    destructiveItems?: DropdownMenuItem[];
}

/**
 * Orders a row's menu by the table rule (design-decisions.md › Zeilenaktionen
 * in Tabellen, PO 2026-11-03): the changing actions first, the destructive
 * ones last, separated by a divider. Hidden items are dropped BEFORE the
 * divider is placed, so a hidden last item cannot take the divider with it.
 */
export const orderRowActions = (
    items: DropdownMenuItem[],
    destructiveItems: DropdownMenuItem[] = [],
): DropdownMenuItem[] => {
    const regular = items.filter((item) => !item.hidden).map((item) => ({ ...item, divider: false }));
    const destructive = destructiveItems.filter((item) => !item.hidden).map((item) => ({ ...item, divider: false }));
    if (regular.length > 0 && destructive.length > 0) {
        regular[regular.length - 1] = { ...regular[regular.length - 1], divider: true };
    }
    return [...regular, ...destructive];
};

/**
 * The `⋮` menu of a table row — every action that changes something sits
 * here. A row whose menu would be empty gets no `⋮` at all (PO, 2026-11-03).
 *
 * Clicks and key presses inside the menu do not reach the row: a clickable
 * row would otherwise open its detail page underneath the menu (React events
 * bubble through the menu's portal).
 */
const RowActionsMenu = ({ rowName, items, destructiveItems }: RowActionsMenuProps) => {
    const { t } = useTranslation();
    const ordered = orderRowActions(items, destructiveItems);
    if (ordered.length === 0) return null;

    return (
        <span
            className={'inline-flex'}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <DropdownMenu
                trigger={(
                    <button
                        type={'button'}
                        className={'p-1 rounded-md text-foreground/60 hover:bg-foreground/5'}
                        aria-label={t('common.more_actions_for', { name: rowName })}
                    >
                        <MoreVertical className={'h-4 w-4'} />
                    </button>
                )}
                items={ordered}
                menuPosition={'right'}
            />
        </span>
    );
};

export default RowActionsMenu;
